import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { DemoStateInspector } from "@/components/DemoStateInspector";

import { PublicShell } from "@/components/shells/PublicShell";
import { MemberShell } from "@/components/shells/MemberShell";
import { AdminShell } from "@/components/shells/AdminShell";
import { StandaloneShell } from "@/components/shells/StandaloneShell";
import { ComingSoon } from "@/components/ComingSoon";

// Hero P0 screens
import HomePage from "@/pages/public/HomePage";
import ReservationDetailPage from "@/pages/member/ReservationDetailPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import SubmissionDetailPage from "@/pages/admin/SubmissionDetailPage";
import PaymentRunDetailPage from "@/pages/admin/PaymentRunDetailPage";

// Wave 2 — Sequence A
import LiveDealsPage from "@/pages/public/LiveDealsPage";
import ApplicationPage from "@/pages/public/ApplicationPage";
import LoginPage from "@/pages/public/LoginPage";
import OnboardingStatusPage from "@/pages/onboarding/OnboardingStatusPage";
import MemberDashboardPage from "@/pages/member/MemberDashboardPage";
import MemberDealsPage from "@/pages/member/MemberDealsPage";
import MemberDealDetailPage from "@/pages/member/MemberDealDetailPage";
import MyReservationsPage from "@/pages/member/MyReservationsPage";
import PaymentHistoryPage from "@/pages/member/PaymentHistoryPage";

// Wave 3 — Sequence B (Admin ops)
import AdminDealsListPage from "@/pages/admin/AdminDealsListPage";
import AdminDealDetailPage from "@/pages/admin/AdminDealDetailPage";
import DealEditPage from "@/pages/admin/DealEditPage";
import ApplicationsQueuePage from "@/pages/admin/ApplicationsQueuePage";
import ApplicationDetailPage from "@/pages/admin/ApplicationDetailPage";
import LiveApplicationDetailPage from "@/pages/admin/LiveApplicationDetailPage";
import SubmissionsQueuePage from "@/pages/admin/SubmissionsQueuePage";
import PaymentRunsPage from "@/pages/admin/PaymentRunsPage";
import AuditLogPage from "@/pages/admin/AuditLogPage";

// Wave 4 — P1 polish + admin directory + config
import BankCapturePage from "@/pages/onboarding/BankCapturePage";
import KycFlowPage from "@/pages/onboarding/KycFlowPage";
import KycCompletePage from "@/pages/onboarding/KycCompletePage";
import AccountPage from "@/pages/member/AccountPage";
import FAQPage from "@/pages/public/FAQPage";
import AdminMembersPage from "@/pages/admin/AdminMembersPage";
import AdminMemberDetailPage from "@/pages/admin/AdminMemberDetailPage";
import BrandsRulesPage from "@/pages/admin/BrandsRulesPage";
import SettingsPage from "@/pages/admin/SettingsPage";

// Wave 5 — P2 auth flow + contact
import ContactPage from "@/pages/public/ContactPage";
import MfaSetupPage from "@/pages/auth/MfaSetupPage";
import MfaVerifyPage from "@/pages/auth/MfaVerifyPage";
import MfaSuccessPage from "@/pages/auth/MfaSuccessPage";
import AdminLoginPage from "@/pages/auth/AdminLoginPage";
import PasswordResetPage from "@/pages/auth/PasswordResetPage";
import PublicDealDetailPage from "@/pages/public/PublicDealDetailPage";
import OnboardingTermsPage from "@/pages/onboarding/OnboardingTermsPage";
import PasswordChangePage from "@/pages/public/PasswordChangePage";
import WelcomePage from "@/pages/public/WelcomePage";

// Wrap a ComingSoon page inside the chosen shell
const stub = (Shell, props, screenId, title) => (
  <Shell {...props}>
    <ComingSoon screenId={screenId} title={title} />
  </Shell>
);

import { ShieldCheck, Lock, ScrollText, Eye, Users, Banknote } from "lucide-react";

// Admin-toned accent — austere, security-first
const ADMIN_ACCENT = {
  eyebrow: "Admin portal",
  headline: "A monitored, audited surface for the operations team.",
  body: "Every action you take here is logged with your admin ID, IP, and timestamp. Sign in only from approved devices.",
  bullets: [
    { icon: ShieldCheck, text: "MFA mandatory on every session" },
    { icon: Lock,        text: "30-minute idle timeout · IP allowlist enforced" },
    { icon: ScrollText,  text: "Decryption, exports & status changes all audit-logged" },
  ],
  id: "ADM",
  tag: "Restricted access",
};

const ONBOARDING_ACCENT = {
  eyebrow: "Onboarding",
  headline: "A few last steps before your first reservation.",
  body: "We use this stage to verify identity, capture your payout bank account, and set you up for your first deal — usually within 48 hours of approval.",
  bullets: [
    { icon: ShieldCheck, text: "KYC handled by Veriff (selfie + ID document)" },
    { icon: Lock,        text: "Bank account encrypted at rest with AWS KMS" },
    { icon: Eye,         text: "Bank account is masked everywhere except the moment of payout" },
  ],
  id: "ONB",
  tag: "Welcome",
};

const APPLY_ACCENT = {
  eyebrow: "Apply to join",
  headline: "A short form, an honest review.",
  body: "We accept members based on identity, source legitimacy, and volume potential. No fees, no minimum lock-in — you can leave any time. Most applications get a decision within two business days.",
  bullets: [
    { icon: ShieldCheck, text: "Identity verified by Veriff (selfie + ID)" },
    { icon: Users,       text: "First reservation usually open within 48 hours of approval" },
    { icon: Banknote,    text: "We never store the full SSN; last 4 is for KYC sanctions only" },
  ],
  id: "APP",
  tag: "Membership review",
};

// Scroll viewport to top on every pathname change (deal card → detail page,
// any cross-route navigation). Stays put on hash anchors and same-path query
// changes so in-page sections still work.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster richColors position="top-right" />
      <DemoStateInspector />
      <ScrollToTop />
      <Routes>
        {/* ── Public ── */}
        <Route path="/" element={<PublicShell transparentHeader><HomePage /></PublicShell>} />
        <Route path="/deals" element={<PublicShell><LiveDealsPage /></PublicShell>} />
        <Route path="/deals/:dealId" element={<PublicShell><PublicDealDetailPage /></PublicShell>} />
        <Route path="/apply" element={<StandaloneShell accent={APPLY_ACCENT} contentMaxWidth="720px" align="start"><ApplicationPage /></StandaloneShell>} />
        <Route path="/faq" element={<PublicShell><FAQPage /></PublicShell>} />
        <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />

        {/* ── Auth (standalone) ── */}
        <Route path="/login" element={<StandaloneShell helper={<>By signing in you agree to our <a href="/faq" className="underline">Terms</a>.</>}><LoginPage /></StandaloneShell>} />
        <Route path="/admin/login" element={<StandaloneShell accent={ADMIN_ACCENT}><AdminLoginPage /></StandaloneShell>} />
        <Route path="/auth/mfa-setup" element={<StandaloneShell accent={ADMIN_ACCENT}><MfaSetupPage /></StandaloneShell>} />
        <Route path="/auth/mfa-success" element={<StandaloneShell accent={ONBOARDING_ACCENT}><MfaSuccessPage /></StandaloneShell>} />
        <Route path="/auth/mfa-verify" element={<StandaloneShell accent={ADMIN_ACCENT}><MfaVerifyPage /></StandaloneShell>} />
        <Route path="/auth/reset-password" element={<StandaloneShell><PasswordResetPage /></StandaloneShell>} />
        <Route path="/auth/set-password" element={<StandaloneShell><PasswordChangePage /></StandaloneShell>} />
        <Route path="/welcome" element={<WelcomePage />} />

        {/* ── Onboarding ── */}
        <Route path="/onboarding" element={<OnboardingStatusPage />} />
        <Route path="/onboarding/bank" element={<BankCapturePage />} />
        <Route path="/onboarding/terms" element={<OnboardingTermsPage />} />
        <Route path="/onboarding/kyc/:token" element={<KycFlowPage />} />
        <Route path="/onboarding/kyc/:token/complete" element={<KycCompletePage />} />

        {/* ── Member ── */}
        <Route path="/member" element={<Navigate to="/member/dashboard" replace />} />
        <Route path="/member/dashboard" element={<MemberShell><MemberDashboardPage /></MemberShell>} />
        <Route path="/member/deals" element={<MemberShell><MemberDealsPage /></MemberShell>} />
        <Route path="/member/deals/:dealId" element={<MemberShell><MemberDealDetailPage /></MemberShell>} />
        <Route path="/member/reservations" element={<MemberShell><MyReservationsPage /></MemberShell>} />
        <Route path="/member/reservations/:reservationId" element={<MemberShell><ReservationDetailPage /></MemberShell>} />
        <Route path="/member/payments" element={<MemberShell><PaymentHistoryPage /></MemberShell>} />
        <Route path="/member/account" element={<MemberShell><AccountPage /></MemberShell>} />

        {/* ── Admin ── */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard" element={<AdminShell><AdminDashboardPage /></AdminShell>} />
        <Route path="/admin/deals" element={<AdminShell><AdminDealsListPage /></AdminShell>} />
        <Route path="/admin/deals/new" element={<AdminShell><DealEditPage /></AdminShell>} />
        <Route path="/admin/deals/:dealId" element={<AdminShell><AdminDealDetailPage /></AdminShell>} />
        <Route path="/admin/deals/:dealId/edit" element={<AdminShell><DealEditPage /></AdminShell>} />
        <Route path="/admin/applications" element={<AdminShell><ApplicationsQueuePage /></AdminShell>} />
        <Route path="/admin/applications/live/:appId" element={<AdminShell><LiveApplicationDetailPage /></AdminShell>} />
        <Route path="/admin/applications/:appId" element={<AdminShell><ApplicationDetailPage /></AdminShell>} />
        <Route path="/admin/submissions" element={<AdminShell><SubmissionsQueuePage /></AdminShell>} />
        <Route path="/admin/submissions/:submissionId" element={<AdminShell><SubmissionDetailPage /></AdminShell>} />
        <Route path="/admin/payments" element={<AdminShell><PaymentRunsPage /></AdminShell>} />
        <Route path="/admin/payments/:runId" element={<AdminShell><PaymentRunDetailPage /></AdminShell>} />
        <Route path="/admin/audit" element={<AdminShell><AuditLogPage /></AdminShell>} />
        <Route path="/admin/members" element={<AdminShell><AdminMembersPage /></AdminShell>} />
        <Route path="/admin/members/:memberId" element={<AdminShell><AdminMemberDetailPage /></AdminShell>} />
        <Route path="/admin/brands" element={<AdminShell><BrandsRulesPage /></AdminShell>} />
        <Route path="/admin/settings" element={<AdminShell><SettingsPage /></AdminShell>} />

        {/* ── Catch-all ── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
